import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import type { OrderBy } from '@/lib/graphql-query-builder';
import { REVIEW_QUERY_KEYS, ReviewFilterValue } from '@/modules/performance/hr-review-core';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import {
  getMbmProjectsByIds,
  getSubmission,
  getSubmissions,
  searchMbmProjects,
  submitReview,
  SubmitReviewParams,
} from '../services/submission.service';

const SUBMISSIONS_KEY = 'hr-my-submissions';

export const useGetSubmissions = (
  userId: string,
  filters: ReviewFilterValue,
  paging: {
    pageNo: number;
    pageSize: number;
    order?: OrderBy[];
    columnWhere?: Record<string, unknown>;
  }
) =>
  useGlobalQuery({
    queryKey: [SUBMISSIONS_KEY, userId, filters, paging],
    queryFn: () => getSubmissions(userId, filters, paging),
    enabled: Boolean(userId),
    staleTime: 30 * 1000,
  });

export const useGetSubmission = (detailId?: string) =>
  useGlobalQuery({
    queryKey: [SUBMISSIONS_KEY, 'detail', detailId],
    queryFn: () => getSubmission(detailId as string),
    enabled: Boolean(detailId),
  });

export const useMbmProjectSearch = (keyword: string) =>
  useGlobalQuery({
    queryKey: [SUBMISSIONS_KEY, 'projects', keyword],
    queryFn: () => searchMbmProjects(keyword),
    staleTime: 5 * 60 * 1000,
  });

export const useMbmProjectsByIds = (projectIds: string[]) =>
  useGlobalQuery({
    queryKey: [SUBMISSIONS_KEY, 'projects-by-id', projectIds],
    queryFn: () => getMbmProjectsByIds(projectIds),
    enabled: projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

export const useSubmitReview = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: SubmitReviewParams) => submitReview(params),
    onSuccess: async () => {
      await Promise.all(
        [
          SUBMISSIONS_KEY,
          REVIEW_QUERY_KEYS.reviews,
          REVIEW_QUERY_KEYS.review,
          REVIEW_QUERY_KEYS.details,
          REVIEW_QUERY_KEYS.detail,
          REVIEW_QUERY_KEYS.reviewDetails,
        ].map((queryKey) => queryClient.invalidateQueries({ queryKey: [queryKey] }))
      );
      toast({ title: t('REVIEW_SUBMITTED') });
    },
  });
};
